import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Stack,
  Box,
  Paper,
  Typography,
  Divider,
  Collapse,
} from "@mui/material";
import { databases, ID } from "../lib/appwrite";
import { Query } from "appwrite";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID;
const COLLECTION_ID = process.env.REACT_APP_APPWRITE_COLLECTION_ID;

const STATUS_OPTIONS = [
  "Pending",
  "Processing",
  "In Transit",
  "At Customs",
  "Out for Delivery",
  "Delivered",
  "Delayed",
  "Exception",
  "Returned",
  "Cancelled",
];

export default function ShipmentForm({ editing, setEditing, onSaved }) {
  const initial = {
    trackingNumber: "",
    customerName: "",
    destinationCity: "",
    status: "Pending",
    details: "",
    records: [],
  };

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [showRecords, setShowRecords] = useState(false);
  const [manualEdit, setManualEdit] = useState(false);
  useEffect(() => {
    if (editing) {
      setForm({
        trackingNumber: editing.trackingNumber || "",
        customerName: editing.customerName || "",
        destinationCity: editing.destinationCity || "",
        status: editing.status || "Pending",
        details: editing.details || "",
        records: editing.records || [],
      });
      setManualEdit(false);
      setShowRecords(false);
    }
  }, [editing]);

  const formatRecord = (status, details) => {
    const now = new Date();
    const date = now.toLocaleDateString("en-GB");
    const time = now.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${date} ${time} - ${status} - ${details}`;
  };

  const parseRecord = (record) => {
    const parts = record.split(" - ");
    if (parts.length >= 3) {
      const dateTime = parts[0].split(" ");
      const date = dateTime[0];
      const time = dateTime[1];
      const status = parts[1];
      const details = parts.slice(2).join(" - ");

      return { date, time, status, details };
    }
    return {};
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setForm((prev) => ({
      ...prev,
      status: newStatus,
    }));
  };

  const handleDetailsChange = (e) => {
    const newDetails = e.target.value;
    setForm((prev) => ({
      ...prev,
      details: newDetails,
    }));
  };

  const handleRecordsChange = (e) => {
    const newRecords = e.target.value
      .split("\n")
      .filter((line) => line.trim() !== "");

    setForm((prev) => ({
      ...prev,
      records: newRecords,
    }));

    setManualEdit(true);

    if (newRecords.length > 0) {
      const last = parseRecord(newRecords[newRecords.length - 1]);
      setForm((prev) => ({
        ...prev,
        status: last.status || prev.status,
        details: last.details || prev.details,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updatedRecords = [...form.records];

      if (!manualEdit) {
        const lastRecord = updatedRecords.length
          ? updatedRecords[updatedRecords.length - 1]
          : null;

        const expectedRecord = formatRecord(form.status, form.details);

        if (lastRecord !== expectedRecord) {
          updatedRecords = [...updatedRecords, expectedRecord];
        }
      }

      if (editing && editing.$id) {
        const related = await databases.listDocuments(DB_ID, COLLECTION_ID, [
          Query.equal("trackingNumber", form.trackingNumber),
        ]);

        for (const doc of related.documents) {
          const hasChanges =
            form.status !== doc.status ||
            form.details !== doc.details ||
            form.customerName !== doc.customerName ||
            form.destinationCity !== doc.destinationCity ||
            JSON.stringify(updatedRecords) !== JSON.stringify(doc.records);

          if (hasChanges) {
            await databases.updateDocument(DB_ID, COLLECTION_ID, doc.$id, {
              customerName: form.customerName,
              destinationCity: form.destinationCity,
              status: form.status,
              details: form.details,
              records: updatedRecords,
            });
          }
        }
      } else {
        const existing = await databases.listDocuments(DB_ID, COLLECTION_ID, [
          Query.equal("trackingNumber", form.trackingNumber),
        ]);

        if (existing.total > 0) {
          alert("A shipment with this tracking number already exists!");
          setLoading(false);
          return;
        }

        const firstRecord = manualEdit
          ? form.records
          : [formatRecord(form.status, form.details)];

        await databases.createDocument(DB_ID, COLLECTION_ID, ID.unique(), {
          ...form,
          records: firstRecord,
        });
      }

      resetForm();
      onSaved?.();
    } catch (err) {
      alert("Failed to save: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initial);
    setEditing(null);
    setShowRecords(false);
    setManualEdit(false);
  };

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        {editing ? "Edit Shipment" : "Add New Shipment"}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label="Tracking Number"
            name="trackingNumber"
            value={form.trackingNumber}
            onChange={handleChange}
            fullWidth
            required
            disabled={!!editing}
          />
          <TextField
            label="Customer Name"
            name="customerName"
            value={form.customerName}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Destination City"
            name="destinationCity"
            value={form.destinationCity}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            select
            label="Status"
            name="status"
            value={form.status}
            onChange={handleStatusChange}
            fullWidth
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Details"
            name="details"
            value={form.details}
            onChange={handleDetailsChange}
            fullWidth
            multiline
            minRows={2}
          />

          {editing && (
            <>
              <Button
                variant="outlined"
                startIcon={
                  showRecords ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                onClick={() => setShowRecords((prev) => !prev)}
              >
                {showRecords ? "Hide Records" : "Edit Records Manually"}
              </Button>
              <Collapse in={showRecords}>
                <TextField
                  label="Records (one per line, format: dd/mm/yyyy hh:mm - Status - Details)"
                  name="records"
                  value={form.records.join("\n")}
                  onChange={handleRecordsChange}
                  fullWidth
                  multiline
                  minRows={5}
                  sx={{ mt: 1 }}
                  placeholder="e.g. 01/10/2025 14:30 - Delivered - Package received at destination"
                />
              </Collapse>
            </>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={resetForm} disabled={loading}>
              Clear
            </Button>
            <Button variant="contained" type="submit" disabled={loading}>
              {editing ? "Save Changes" : "Add Shipment"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
